<template>
	<button
		v-if="isVisibleOnFeatureSwitch"
		:id="uid"
		@click="onClick"
		:class="[
			'btn-action-ribbon',
			...variantClassList,
			...stateClassList,
			disabled && 'is-disabled',
			hasUnsavedChanges && 'has-unsaved-changes',
			disabled
				? 'is-disabled'
				: isLoading
				? 'is-disabled'
				: hasLoaded
				? 'is-disabled'
				: '',
			isLoading && 'is-loading',
			hasLoaded && 'has-loaded'
		]"
		:title="title"
		:disabled="disabled"
		:type="type"
		ref="buttonActionRibbon"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
		@focus="onMouseEnter"
		@blur="onBlur"
	>
		<span class="btn-action-ribbon__icon" v-if="iconClassName.length > 0">
			<i class="icon" :class="iconClassName" />
			<Badge
				class="btn-action-ribbon__unsave-changes-badge"
				:visible="hasUnsavedChanges"
				state="negative"
				variant="pill"
				:text="badgeText"
			>
			</Badge>
		</span>
		<!-- @slot Use this slot to customize the button content -->
		<slot>
			<span
				v-if="labelText"
				class="btn-action-ribbon__label"
				data-id="label-text"
			>
				{{ labelText }}
			</span>
		</slot>
		<Tooltip
			placement="right"
			:visible="displayTooltip"
			trackedRefName="buttonActionRibbon"
			positionStrategy="fixed"
		>
			{{ tooltipText }}
		</Tooltip>
		<ButtonProgress
			:isLoading="isLoading"
			:hasLoaded="hasLoaded"
			:hasError="hasError"
			:errorMessage="errorMessage"
		/>
	</button>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import baseButtonMixin from '@/mixins/base-button-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/button-action-ribbon-variant-classes';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import Badge from '@/components/shells/badge/badge.vue';
import ButtonProgress from '@/components/visual/loader/button-progress.vue';
import componentStateMixin from '@/mixins/component-state-mixin';

/**
 *
 * ButtonActionRibbon
 * @param {('icon'|'save')} variant - the button's visual variation
 */
export default {
	name: 'ButtonActionRibbon',
	mixins: [
		baseComponentMixin,
		baseButtonMixin,
		componentVariantMixin,
		componentStateMixin
	],
	components: {
		Tooltip,
		Badge,
		ButtonProgress
	},
	created() {
		this.variantClasses = variantClasses;
	},
	data() {
		return {
			displayTooltip: false,
			btnOnFocus: false
		};
	},
	props: {
		hasUnsavedChanges: {
			type: Boolean,
			default: false
		},
		tooltipText: {
			type: String,
			default: 'No Unsaved Changes'
		},
		badgeText: {
			type: String,
			default: '!' // default value, can adjust it as needed
		}
	},
	methods: {
		onBlur() {
			this.btnOnFocus = false;
			this.displayTooltip = false;
		},
		onMouseEnter() {
			if (
				(this.variant === 'save' || this.variant === 'iconsave') &&
				!this.hasUnsavedChanges
			) {
				this.displayTooltip = true;
			}
		},
		onMouseLeave() {
			if (
				(this.variant === 'save' || this.variant === 'iconsave') &&
				!this.hasUnsavedChanges &&
				this.pillOnFocus
			) {
				this.displayTooltip = true;
			} else {
				this.displayTooltip = false;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.btn-action-ribbon {
	position: relative;
	@include btn-clear;
	display: inline-flex;
	align-items: center;
	padding: $btn-padding-y 0;
	border-radius: $border-radius;
	font-size: $btn-reduced-font-size;
	border: $btn-border-width solid transparent;

	&__unsave-changes-badge {
		position: absolute;
		bottom: 7px;
		left: 7px;
		font-style: italic;

		&:deep(.badge__pill) {
			height: 18px;
			width: 18px;
			min-width: 18px;
		}
	}

	&__unsaved-changes-tooltip {
		display: none;
		position: absolute;
		bottom: 85%;
		left: 50%;
		transform: translateX(-50%);
	}

	&__icon {
		display: inline-flex;
		align-items: center;
		position: relative;
		margin-right: $gutter-md;
		font-size: 18px;
		height: 18px;

		&.no-label-text {
			margin-right: 0;
		}
	}

	&__label {
		text-transform: uppercase;
		border-bottom: $btn-border-width solid transparent;
		height: 18px;
		line-height: 18px;
	}

	&--icon-only-save {
		.btn-action-ribbon__unsave-changes-badge {
			bottom: 12px;
			left: 12px;
		}
	}

	&--icon-only,
	&--icon-only-save {
		min-width: 32px;
		justify-content: center;
		padding: $padding-xs;

		.btn-action-ribbon {
			&__icon {
				font-size: 26px;
				height: 26px;
				margin-right: 0;
			}

			.btn-action-ribbon__label {
				display: none;
			}
		}

		&.btn-action-ribbon--save,
		&.btn-action-ribbon--icon-only-save {
			cursor: default;

			.btn-action-ribbon__icon,
			.btn-action-ribbon__label {
				opacity: $disabled-opacity;
			}

			&:hover,
			&:focus,
			&:active {
				.btn-action-ribbon__icon,
				.btn-action-ribbon__label {
					border-color: transparent;
				}

				.btn-action-ribbon__unsaved-changes-tooltip.no-unsaved-changes {
					display: inline-block;
				}
			}

			&.has-unsaved-changes {
				cursor: pointer;

				.btn-action-ribbon__icon,
				.btn-action-ribbon__label {
					opacity: 1;
				}
			}
		}
	}

	&.btn-action-ribbon--save,
	&.btn-action-ribbon--icon-only-save {
		cursor: default;

		.btn-action-ribbon__icon,
		.btn-action-ribbon__label {
			opacity: $disabled-opacity;
		}

		&.has-unsaved-changes {
			cursor: pointer;

			.btn-action-ribbon__icon,
			.btn-action-ribbon__label {
				opacity: 1;
			}
		}

		&:hover,
		&:focus,
		&:active {
			.btn-action-ribbon__icon,
			.btn-action-ribbon__label {
				border-color: transparent;
			}
		}
	}

	& :deep(.badge) {
		.badge__pill {
			border-color: transparent;
		}
	}
}

/* LL styles v1 Only */
.btn-action-ribbon {
	color: var(--bg-2-fg-3);

	&:hover,
	&:focus,
	&:active {
		.btn-action-ribbon__icon,
		.btn-action-ribbon__label {
			color: var(--link-hover);
		}

		.btn-action-ribbon__label {
			border-color: var(--link-hover);
		}
	}

	&--icon-only {
		&:hover {
			background: var(--neutral-2);
		}

		&:focus,
		&:active {
			&::after {
				content: '';
				@include transform-vertical-center;
				left: 0;
				transform: translateY(-50%);
				width: calc(100% + 1px);
				height: calc(100% + 1px);
				border-radius: $border-radius;
				border: $focus-width $focus-style var(--neutral-3);
				pointer-events: none;
			}
		}

		&:hover,
		&:focus,
		&:active {
			.btn-action-ribbon__icon,
			.btn-action-ribbon__label {
				color: var(--bg-2-fg-3);
			}
		}
	}

	&--save {
		&:hover,
		&:focus,
		&:active {
			.btn-action-ribbon__icon,
			.btn-action-ribbon__label {
				color: var(--bg-2-fg-3);
			}
		}

		&.has-unsaved-changes {
			&:hover,
			&:focus,
			&:active {
				.btn-action-ribbon__icon,
				.btn-action-ribbon__label {
					color: var(--link-hover);
				}

				.btn-action-ribbon__label {
					border-color: var(--link-hover);
				}
			}
		}
	}
	&--icon-only-save {
		&:hover,
		&:focus,
		&:active {
			.btn-action-ribbon__icon {
				color: var(--bg-2-fg-3);
			}
		}
		&.has-unsaved-changes {
			&:hover {
				background: var(--brand-2-tint);
			}
			&:focus,
			&:active {
				&::after {
					content: '';
					@include transform-vertical-center;
					left: 0;
					transform: translateY(-50%);
					width: calc(100% + 1px);
					height: calc(100% + 1px);
					border-radius: $border-radius;
					border: $focus-width $focus-style var(--neutral-3);
					pointer-events: none;
				}
			}

			&:hover,
			&:focus,
			&:active {
				.btn-action-ribbon__icon,
				.btn-action-ribbon__label {
					color: var(--bg-2-fg-3);
				}
			}
		}
	}
}

/* LL styles v2 Only */
/* .btn-action-ribbon {
	color: var(--bg-brand-2-fg-2);

	&:hover,
	&:focus,
	&:active {
		.btn-action-ribbon__label {
			border-color: var(--bg-0);
		}
	}

	&--icon-only {

		&:hover,
		&:focus,
		&:active {
			background: var(--brand-2-tint);
		}
	}

	&--save {
		padding: $btn-padding-y $padding-sm;

		&.has-unsaved-changes {
			background: var(--bg-0);
			color: var(--brand-1);

			&:hover,
			&:focus,
			&:active {
				.btn-action-ribbon__icon,
				.btn-action-ribbon__label {
					color: var(--brand-1);
				}

				.btn-action-ribbon__label {
					border-color: var(--brand-1);
				}
			}
		}
	}

	&--save,
	&--icon-only-save {
		&.has-unsaved-changes {
			.btn-action-ribbon__icon {
				position: relative;
				&:after {
					content: '';
					position: absolute;
					top: -4px;
					right: -6px;
					content: '';
					height: 14px;
					width: 14px;
					border: $focus-width $focus-style var(--ui-negative);
					border-radius: 50%;
					background-color: var(--ui-negative);
				}
			}
		}
	}

	&--icon-only-save {
		&.has-unsaved-changes {
			&:hover,
			&:focus,
			&:active {
				background: var(--brand-2-tint);
			}
		}
	}

	.badge {
		display: none;
	}
} */
</style>
